import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { apiCall, publicApiCall } from './utils/api';

// Import new page components
import AuditsPage from './components/AuditsPage';
import AuditDetailPage from './components/AuditDetailPage';
import EnhancedAuditDetail from './components/EnhancedAuditDetail';
import ReportsPage from './components/ReportsPage';
import SafetyAnalysisPageV2 from './components/SafetyAnalysisPageV2';
import AdvancedSafetyAI from './components/AdvancedSafetyAI';
import SafetyOperationsCenter from './components/SafetyOperationsCenter';
import SafetyMonitoringHub from './components/SafetyMonitoringHub';
import AnalysisDetailPage from './components/AnalysisDetailPage';
import MovedToInductIQ from './components/MovedToInductIQ';
import { WsafeSignInButton, WsafeAuthCallback } from './components/WsafeSignIn';
import LiveMonitoringPage from './components/LiveMonitoringPage';
import AdvancedToolsPage from './components/AdvancedToolsPage';
import KnowledgeCenterPage from './components/KnowledgeCenterPage';
import EnhancedKnowledgeCenter from './components/EnhancedKnowledgeCenter';
import SubscriptionPage from './components/SubscriptionPage';
import PaymentSuccessPage from './components/PaymentSuccessPage';
import PaymentCanceledPage from './components/PaymentCanceledPage';
import BillingManagementPage from './components/BillingManagementPage';
import PricingPage from './components/PricingPage';
import BillingPage from './components/BillingPage';
import SupportPage from './components/SupportPage';
import SubscriptionGate from './components/SubscriptionGate';
import DocumentManagement from './components/DocumentManagement';
import AdminDashboard from './components/AdminDashboard';
import SafetyFactBanner from './components/SafetyFactBanner';
import HelpCenterPage from './components/HelpCenterPage';
import EnhancedHelpCenter from './components/EnhancedHelpCenter';
import OSHARegulationsSearch from './components/OSHARegulationsSearch';
import CompaniesPage from './components/CompaniesPage';
import Dashboard from './components/Dashboard';
import ROIDashboard from './components/ROIDashboard';
import PredictiveAnalytics from './components/PredictiveAnalytics';
import TranslationHub from './components/TranslationHub';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import SafetyWorkManagement from './components/SafetyWorkManagement';
import GlobalSearch from './components/GlobalSearch';
import ObservationsPage from './components/ObservationsPage';
import MeetingsPage from './components/MeetingsPage';
import AnalyticsIntelligenceDashboard from './components/AnalyticsIntelligenceDashboard';
import GamificationDashboard from './components/GamificationDashboard';
import VoiceCommands from './components/VoiceCommands';
import IntegrationsHub from './components/IntegrationsHub';
import RoleManagement from './components/RoleManagement';
import ScheduledReportsPage from './components/ScheduledReportsPage';
import ProductTourAnalyticsDashboard from './components/ProductTourAnalyticsDashboard';
import AIAssistantPage, { FloatingChatButton } from './components/AIAssistant';
import SafetyScorecardPage from './components/SafetyScorecardPage';
import NearMissReportingPage from './components/NearMissReportingPage';
import SafetyCalendarPage from './components/SafetyCalendarPage';
import HazardAlertsPage from './components/HazardAlertsPage';
import ToolboxTalksPage from './components/ToolboxTalksPage';
import ContractorManagementPage from './components/ContractorManagementPage';
import ChemicalInventoryPage from './components/ChemicalInventoryPage';
// Public Landing Page
import PublicLandingPage from './components/PublicLandingPage';
// Quick Win Features - Phase 2
import DailyPTPPage from './components/DailyPTPPage';
import EquipmentLogPage from './components/EquipmentLogPage';
import QuickHazardPage from './components/QuickHazardPage';
import SuggestionBoxPage from './components/SuggestionBoxPage';
import AIHazardScanPage from './components/AIHazardScanPage';
import SharedScanPage from './components/SharedScanPage';
// Phase 2 - Comprehensive Features
import JHABuilderPage from './components/JHABuilderPage';
import SafetyAnalyticsPage from './components/SafetyAnalyticsPage';
import PermitToWorkPage from './components/PermitToWorkPage';
// CAPA Management
import CAPAPage from './components/CAPAPage';
// Safety Programs
import SafetyProgramsPage from './components/SafetyProgramsPage';
// OSHA Checklists
import OSHAChecklistsPage from './components/OSHAChecklistsPage';
// Risk Matrix
import RiskMatrix from './components/RiskMatrix';
// Risk Trends
import RiskTrendsPage from './components/RiskTrendsPage';
// AI Enhanced Safety
import AIEnhancedSafetyPage from './components/AIEnhancedSafetyPage';
// Phase 3 - Digital Signatures & Compliance
import ComplianceCalendarPage from './components/ComplianceCalendarPage';
// Phase 2 - Notifications
import { NotificationBell } from './components/NotificationCenter';
import NotificationSettingsPage from './components/NotificationSettingsPage';
// Phase 4 - Advanced Analytics
import DashboardBuilder from './components/DashboardBuilder';
// Phase 5 - Integrations & Automation
import EmployeeImportPage from './components/EmployeeImportPage';
import IoTSensorDashboard from './components/IoTSensorDashboard';
import WorkflowAutomationPage from './components/WorkflowAutomationPage';
// Project Management
import ProjectManagementPage from './components/ProjectManagementPage';
// First Report of Injury
// Safety Meetings
import SafetyMeetingsPage from './components/SafetyMeetingsPage';
// Company Management (Admin)
import CompanyManagementPage from './components/CompanyManagementPage';
import CompanyDetailPage from './components/CompanyDetailPage';
// Alerts & Reports
import AlertsReportsPage from './components/AlertsReportsPage';
// Phase 6 - Mobile-First / Field Mode
import { FieldModeProvider, useFieldMode } from './context/FieldModeContext';
import { FieldModeToggle, SyncStatusBanner, OfflineIndicator as FieldModeOfflineIndicator } from './components/FieldModeComponents';
import FieldModeDashboard from './components/FieldModeDashboard';
import MobilePTPForm from './components/MobilePTPForm';
import MobileNearMissForm from './components/MobileNearMissForm';
import MobileJHAForm from './components/MobileJHAForm';

// Phase 8 - Advanced Reporting & Communication
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import AuditTrailPage from './pages/AuditTrailPage';
import ReportGeneratorPage from './pages/ReportGeneratorPage';
import EmailNotificationsPage from './pages/EmailNotificationsPage';
import UserProfileSettings from './pages/UserProfileSettings';
import CCTVMonitoringPage from './pages/CCTVMonitoringPage';
import SafetyAlertsDashboard from './pages/SafetyAlertsDashboard';
import AlertIntegrationsPage from './pages/AlertIntegrationsPage';
import AIInsightsPage from './pages/AIInsightsPage';
import SafetyScorecardExportPage from './pages/SafetyScorecardExportPage';

// Context Providers
import { ThemeProvider, useTheme } from './context/ThemeContext';

// Advanced Search
import AdvancedSearch from './components/AdvancedSearch';

// UI Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { Progress } from './components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { useToast } from './hooks/use-toast';
import { Toaster } from './components/ui/toaster';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';

// Icons
import { Shield, Users, FileText, BarChart3, AlertTriangle, CheckCircle, Clock, DollarSign, Settings, LogOut, Plus, Search, Filter, Calendar, TrendingUp, Target, Award, Building, Eye, Brain, Zap, Key, Mail, Camera, Upload, Scan, Image, Video, Sparkles, HelpCircle, GraduationCap, ExternalLink, BookOpen, Calculator, Activity, Languages, ClipboardList, Menu, X, Monitor, Radio, Trophy, Mic, Plug, UserCog, FolderOpen, Smartphone, MessageSquare, Beaker, HardHat, Wrench, Lightbulb, FileSearch, Lock, Flame, CreditCard, ChevronDown, Home, FileWarning, AlertCircle, ClipboardCheck, FileSpreadsheet, Bell, ArrowRight, LayoutGrid, GitBranch, Scale, Play, XCircle, Star } from 'lucide-react';

// PWA Components
import { PWAProvider, OfflineIndicator, SyncStatus } from './components/PWAComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tour steps data (outside component to prevent recreation)
const TOUR_STEPS = [
  {
    id: 1,
    title: "Track Everything",
    subtitle: "Real-time Safety Dashboard",
    description: "Get a complete overview of your safety performance with live metrics, incident tracking, and compliance status all in one place.",
    image: "https://static.prod-images.emergentagent.com/jobs/83c3b611-7662-4499-bd96-86e6af7c2ffa/images/cdabad2940d8b7dafe8cbf5cfe808cec65371ff46adbbf8ea6b4f40a8415b551.png",
    gradient: "from-blue-600 to-cyan-600",
    badgeClass: "bg-blue-500/10 text-blue-400",
    iconClass: "bg-blue-500/20 text-blue-400",
    subtitleClass: "text-blue-400",
    features: ["Live safety score tracking", "Incident trend analysis", "Compliance calendar", "Automated reporting"]
  },
  {
    id: 2,
    title: "AI Hazard Detection",
    subtitle: "Instant Photo Analysis",
    description: "Upload any worksite photo and our AI instantly identifies safety hazards, maps them to OSHA regulations, and provides actionable recommendations.",
    image: "https://static.prod-images.emergentagent.com/jobs/83c3b611-7662-4499-bd96-86e6af7c2ffa/images/ee46d4fd944949e192686db0b6d2093eeee18a7206368f42aa49c06e9efc70b7.png",
    gradient: "from-purple-600 to-pink-600",
    badgeClass: "bg-purple-500/10 text-purple-400",
    iconClass: "bg-purple-500/20 text-purple-400",
    subtitleClass: "text-purple-400",
    features: ["Instant hazard identification", "OSHA regulation mapping", "Severity assessment", "Actionable recommendations"]
  },
  {
    id: 3,
    title: "Work Anywhere",
    subtitle: "Mobile Field Mode",
    description: "Take safety management to the field with our offline-first mobile app. Report incidents, conduct audits, and capture hazards even without internet.",
    image: "https://static.prod-images.emergentagent.com/jobs/83c3b611-7662-4499-bd96-86e6af7c2ffa/images/780e398d6fd9b87780c121716f3546b5b824b9affa45d8854973ed9ba6497295.png",
    gradient: "from-green-600 to-emerald-600",
    badgeClass: "bg-green-500/10 text-green-400",
    iconClass: "bg-green-500/20 text-green-400",
    subtitleClass: "text-green-400",
    features: ["Offline-first design", "Voice note capture", "GPS location tagging", "Auto-sync when online"]
  }
];

// Product Tour Carousel Component with Analytics
const ProductTourCarousel = ({ setIsLogin }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sessionId] = useState(() => `tour_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [stepStartTime, setStepStartTime] = useState(Date.now());
  const [viewedSteps, setViewedSteps] = useState(new Set([0]));
  const tourSteps = TOUR_STEPS;

  // Analytics tracking function
  const trackEvent = useCallback(async (eventType, stepId = null, stepTitle = null, timeOnStep = null, metadata = {}) => {
    try {
      await axios.post(`${API}/product-tour-analytics/track`, {
        event_type: eventType,
        step_id: stepId,
        step_title: stepTitle,
        time_on_step: timeOnStep,
        session_id: sessionId,
        source: window.innerWidth < 768 ? 'mobile' : 'web',
        metadata
      });
    } catch (error) {
      // Silent fail - don't disrupt user experience for analytics
      console.debug('Analytics tracking failed:', error);
    }
  }, [sessionId]);

  // Track initial step view on mount
  useEffect(() => {
    trackEvent('step_view', 1, TOUR_STEPS[0].title);
  }, [trackEvent]);

  // Auto-advance timer with analytics
  useEffect(() => {
    if (isPaused) return;
    
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          // Calculate time spent on current step
          const timeOnStep = (Date.now() - stepStartTime) / 1000;
          const currentStepData = tourSteps[activeStep];
          
          // Track step completion with time
          trackEvent('step_view', currentStepData.id, currentStepData.title, timeOnStep);
          
          const nextStep = (activeStep + 1) % tourSteps.length;
          setActiveStep(nextStep);
          setStepStartTime(Date.now());
          
          // Track if tour completed (viewed all 3 steps)
          const newViewedSteps = new Set(viewedSteps);
          newViewedSteps.add(nextStep);
          setViewedSteps(newViewedSteps);
          
          if (newViewedSteps.size === tourSteps.length && !viewedSteps.has(nextStep)) {
            trackEvent('tour_complete', null, null, null, { total_steps: tourSteps.length });
          }
          
          return 0;
        }
        return prev + 2;
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, [isPaused, tourSteps.length, activeStep, stepStartTime, trackEvent, viewedSteps, tourSteps]);

  // Track manual step navigation
  const goToStep = (index) => {
    const timeOnStep = (Date.now() - stepStartTime) / 1000;
    const currentStepData = tourSteps[activeStep];
    
    // Track leaving current step
    trackEvent('step_click', currentStepData.id, currentStepData.title, timeOnStep, { 
      navigation_type: 'manual',
      from_step: activeStep + 1,
      to_step: index + 1
    });
    
    setActiveStep(index);
    setProgress(0);
    setStepStartTime(Date.now());
    
    // Update viewed steps
    const newViewedSteps = new Set(viewedSteps);
    newViewedSteps.add(index);
    setViewedSteps(newViewedSteps);
  };

  // Track pause/resume
  const handleMouseEnter = () => {
    setIsPaused(true);
    trackEvent('carousel_pause', tourSteps[activeStep].id, tourSteps[activeStep].title);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
    trackEvent('carousel_resume', tourSteps[activeStep].id, tourSteps[activeStep].title);
  };

  // Track CTA click
  const handleCtaClick = () => {
    const timeOnStep = (Date.now() - stepStartTime) / 1000;
    trackEvent('cta_click', tourSteps[activeStep].id, tourSteps[activeStep].title, timeOnStep, {
      cta_text: 'Apply for Pilot',
      steps_viewed: Array.from(viewedSteps).map(i => i + 1)
    });
    setIsLogin(false);
  };

  const currentStep = tourSteps[activeStep];

  return (
    <div 
      className="max-w-6xl mx-auto"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid="product-tour-carousel"
    >
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
          <Play className="w-4 h-4" />
          Interactive Demo
          {isPaused && <span className="text-xs text-slate-500 ml-2">(Paused)</span>}
        </div>
        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
          See WSafe AI in Action
        </h2>
        <p className="text-slate-400 max-w-2xl mx-auto text-lg">
          Discover how our platform transforms safety management in just 3 simple steps
        </p>
      </div>

      {/* Step Indicators with Progress */}
      <div className="flex justify-center gap-4 mb-8" data-testid="tour-step-indicators">
        {tourSteps.map((step, index) => (
          <button
            key={step.id}
            onClick={() => goToStep(index)}
            data-testid={`tour-step-${step.id}`}
            className={`relative flex items-center gap-3 px-6 py-3 rounded-xl transition-all duration-300 ${
              activeStep === index 
                ? `bg-gradient-to-r ${step.gradient} text-white shadow-lg` 
                : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50'
            }`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${
              activeStep === index ? 'bg-white/20' : 'bg-slate-700'
            }`}>
              {step.id}
            </div>
            <span className="font-medium hidden sm:block">{step.title}</span>
            
            {/* Progress bar for active step */}
            {activeStep === index && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20 rounded-b-xl overflow-hidden">
                <div 
                  className="h-full bg-white/40 transition-all duration-100"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Main Content Area */}
      <div className="grid lg:grid-cols-2 gap-8 items-center mb-12">
        {/* Image Side */}
        <div className="relative group order-2 lg:order-1">
          <div className={`absolute -inset-4 bg-gradient-to-r ${currentStep.gradient} rounded-3xl blur-xl opacity-30 group-hover:opacity-50 transition duration-500`}></div>
          <div className="relative rounded-2xl overflow-hidden border border-slate-700 shadow-2xl">
            {tourSteps.map((step, index) => (
              <div
                key={step.id}
                className={`transition-all duration-700 ${
                  activeStep === index 
                    ? 'opacity-100 scale-100' 
                    : 'opacity-0 scale-95 absolute inset-0'
                }`}
              >
                <img 
                  src={step.image}
                  alt={step.title}
                  className="w-full h-auto"
                />
              </div>
            ))}
          </div>
          
          {/* Navigation Arrows */}
          <button
            onClick={() => goToStep((activeStep - 1 + tourSteps.length) % tourSteps.length)}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-slate-900/80 hover:bg-slate-800 rounded-full flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronDown className="w-6 h-6 rotate-90" />
          </button>
          <button
            onClick={() => goToStep((activeStep + 1) % tourSteps.length)}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-slate-900/80 hover:bg-slate-800 rounded-full flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronDown className="w-6 h-6 -rotate-90" />
          </button>
        </div>

        {/* Content Side */}
        <div className="order-1 lg:order-2 relative min-h-[400px]">
          {tourSteps.map((step, index) => (
            <div
              key={step.id}
              className={`transition-all duration-500 ${
                activeStep === index 
                  ? 'opacity-100 translate-y-0 relative' 
                  : 'opacity-0 translate-y-4 absolute top-0 left-0 right-0 pointer-events-none'
              }`}
            >
              <div className={`inline-flex items-center gap-2 ${step.badgeClass} px-3 py-1 rounded-full text-sm font-medium mb-4`}>
                Step {step.id} of 3
              </div>
              <h3 className="text-3xl font-bold text-white mb-2">{step.title}</h3>
              <p className={`${step.subtitleClass} text-lg mb-4`}>{step.subtitle}</p>
              <p className="text-slate-300 text-lg leading-relaxed mb-6">{step.description}</p>
              
              <ul className="space-y-3 mb-8">
                {step.features.map((feature, i) => (
                  <li 
                    key={i} 
                    className="flex items-center gap-3 text-slate-300"
                  >
                    <div className={`w-6 h-6 rounded-full ${step.iconClass} flex items-center justify-center`}>
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="text-center">
        <div className="inline-flex flex-col sm:flex-row gap-4 items-center">
          <Button 
            className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-black font-semibold px-8 py-6 text-lg rounded-xl shadow-lg shadow-amber-500/25 hover:scale-105 transition-transform"
            onClick={handleCtaClick}
            data-testid="product-tour-cta"
          >
            Apply for Pilot Program
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-slate-500 text-sm">$1,000 Founding Pilot • Limited Spots</p>
        </div>
      </div>
    </div>
  );
};

// Auth Context
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const clearAuth = useCallback(() => {
    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      delete axios.defaults.headers.common['Authorization'];
    } catch (e) {
      console.warn('Failed to clear auth:', e);
    }
    setUser(null);
  }, []);

  // Initialize auth on mount
  useEffect(() => {
    let isMounted = true;
    
    const initializeAuth = async () => {
      let token, userData;
      
      try {
        token = localStorage.getItem('auth_token');
        userData = localStorage.getItem('user_data');
      } catch (e) {
        console.warn('Failed to read localStorage:', e);
        if (isMounted) setLoading(false);
        return;
      }
      
      if (token && userData) {
        try {
          // Set axios authorization header for legacy code
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Verify token is still valid using global apiCall
          const data = await apiCall('/api/auth/me');
          
          if (isMounted && data.user) {
            setUser(data.user);
          }
        } catch (error) {
          console.warn('Token verification failed:', error);
          // Token validation failed - but don't clear immediately
          // Try to use cached user data as fallback
          try {
            const cachedUser = JSON.parse(userData);
            if (isMounted && cachedUser) {
              setUser(cachedUser);
            }
          } catch (e) {
            if (isMounted) {
              clearAuth();
            }
          }
        }
      }
      if (isMounted) {
        setLoading(false);
      }
    };
    
    initializeAuth();
    
    return () => {
      isMounted = false;
    };
  }, [clearAuth]); // Added clearAuth to deps

  const refreshToken = useCallback(async () => {
    try {
      const data = await apiCall('/api/auth/refresh-token', { method: 'POST' });
      const { token: newToken, user: userData } = data;
      
      localStorage.setItem('auth_token', newToken);
      localStorage.setItem('user_data', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      setUser(userData);
      return true;
    } catch (error) {
      // Token refresh failed - clear auth silently
      clearAuth();
      return false;
    }
  }, [clearAuth]);

  const login = async (email, password) => {
    try {
      // Clear any stale auth state before attempting login
      try {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        delete axios.defaults.headers.common['Authorization'];
      } catch (e) {
        console.warn('Failed to clear auth:', e);
      }
      
      console.log('Login: Attempting for', email);
      
      // Use global publicApiCall for consistency
      const data = await publicApiCall('/api/auth/login', {
        method: 'POST',
        body: { email, password }
      });
      
      const { token, user: userData } = data;
      
      if (!token || !userData) {
        console.error('Login: Missing token or user data in response');
        toast({ 
          title: "Error", 
          description: "Invalid server response. Please try again.",
          variant: "destructive"
        });
        return false;
      }
      
      console.log('Login: Success, storing credentials');
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user_data', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(userData);
      toast({ title: "Success", description: "Logged in successfully!" });
      return true;
    } catch (error) {
      console.error('Login: Error', error.message);
      
      let errorMessage = "Login failed. Please try again.";
      if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
        errorMessage = "Network error. Please check your connection and browser settings.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({ 
        title: "Login Failed", 
        description: errorMessage,
        variant: "destructive"
      });
      return false;
    }
  };

  const register = async (userData) => {
    try {
      const data = await publicApiCall('/api/auth/register', {
        method: 'POST',
        body: userData
      });
      
      const { token, user: newUser } = data;
      
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user_data', JSON.stringify(newUser));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(newUser);
      toast({ title: "Success", description: "Account created successfully!" });
      return true;
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.message || "Registration failed",
        variant: "destructive"
      });
      return false;
    }
  };

  const logout = () => {
    clearAuth();
    toast({ title: "Success", description: "Logged out successfully!" });
  };

  const forgotPassword = async (email) => {
    try {
      const data = await publicApiCall('/api/auth/forgot-password', {
        method: 'POST',
        body: { email }
      });
      
      toast({ 
        title: "Success", 
        description: "Password reset instructions sent to your email" 
      });
      return data;
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.message || "Password reset failed",
        variant: "destructive"
      });
      return null;
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await publicApiCall('/api/auth/reset-password', {
        method: 'POST',
        body: { token, new_password: newPassword }
      });
      
      toast({ 
        title: "Success", 
        description: "Password reset successfully! Please log in with your new password." 
      });
      return true;
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.message || "Password reset failed",
        variant: "destructive"
      });
      return false;
    }
  };

  // Auto-refresh token every 6 hours
  useEffect(() => {
    if (user) {
      const interval = setInterval(() => {
        refreshToken();
      }, 6 * 60 * 60 * 1000); // 6 hours
      
      return () => clearInterval(interval);
    }
  }, [user, refreshToken]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      register, 
      logout, 
      forgotPassword, 
      resetPassword, 
      refreshToken, 
      loading 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Google OAuth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const hasProcessed = React.useRef(false);

  React.useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL - check both hash and query params
        let sessionId = null;
        
        // Try hash first (fragment)
        const hash = window.location.hash;
        if (hash) {
          const hashParams = new URLSearchParams(hash.replace('#', ''));
          sessionId = hashParams.get('session_id');
        }
        
        // Try query params as fallback
        if (!sessionId) {
          const searchParams = new URLSearchParams(window.location.search);
          sessionId = searchParams.get('session_id');
        }
        
        // Also check if it's in the pathname (some browsers might encode it weirdly)
        if (!sessionId && location.pathname.includes('session_id=')) {
          const match = location.pathname.match(/session_id=([^&]+)/);
          if (match) sessionId = match[1];
        }

        console.log('AuthCallback: Processing auth', { 
          hash: hash?.substring(0, 50), 
          search: window.location.search?.substring(0, 50),
          foundSessionId: !!sessionId 
        });

        if (!sessionId) {
          console.error('AuthCallback: No session_id found in URL');
          toast({ title: "Error", description: "No session ID found. Please try again.", variant: "destructive" });
          navigate('/login');
          return;
        }

        console.log('AuthCallback: Calling backend...');
        
        // Call backend to process session using global publicApiCall
        const data = await publicApiCall('/api/auth/google/session', {
          method: 'POST',
          body: { session_id: sessionId }
        });

        if (data.success && data.token && data.user) {
          console.log('AuthCallback: Success, storing credentials');
          
          // Store auth data with verification
          try {
            localStorage.setItem('auth_token', data.token);
            localStorage.setItem('user_data', JSON.stringify(data.user));
            
            // Verify storage worked
            const storedToken = localStorage.getItem('auth_token');
            if (!storedToken) {
              throw new Error('Storage verification failed');
            }
          } catch (storageError) {
            console.error('AuthCallback: Storage error', storageError);
            toast({ 
              title: "Storage Error", 
              description: "Cannot save login. Please enable cookies/localStorage in Edge settings.",
              variant: "destructive" 
            });
            navigate('/login');
            return;
          }
          
          axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;

          toast({ title: "Success", description: `Welcome, ${data.user.name || data.user.username}!` });
          
          // Small delay for Edge stability
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Force page reload to ensure clean state
          window.location.href = '/dashboard';
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('AuthCallback: Error', error);
        toast({ 
          title: "Authentication Failed", 
          description: error.message || "Could not complete Google sign-in. Please try again.",
          variant: "destructive" 
        });
        navigate('/login');
      }
    };

    processAuth();
  }, [location, navigate, toast]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-slate-600">Completing sign-in...</p>
      </div>
    </div>
  );
};

// Settings Page Wrapper - gets user from context
const SettingsPageWrapper = () => {
  const { user, logout } = useAuth();
  return <UserProfileSettings user={user} onLogout={logout} />;
};

// Login Component - Sales-Focused Landing Page
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  
  const { login, register, forgotPassword, resetPassword } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isLogin) {
        // Validate inputs
        const trimmedEmail = email?.trim();
        const trimmedPassword = password?.trim();
        
        if (!trimmedEmail || !trimmedPassword) {
          toast({ title: "Error", description: "Please enter email and password", variant: "destructive" });
          setLoading(false);
          return;
        }
        
        console.log('Login: Starting for', trimmedEmail);
        
        // Clear any stale data before login attempt
        try {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          delete axios.defaults.headers.common['Authorization'];
        } catch (e) {
          console.warn('Could not clear localStorage:', e);
        }
        
        // Use global publicApiCall for consistent handling
        const data = await publicApiCall('/api/auth/login', {
          method: 'POST',
          body: { email: trimmedEmail, password: trimmedPassword }
        });
        
        console.log('Login: Got data', !!data.token);
        
        if (data.token && data.user) {
          // Store credentials with error handling
          try {
            localStorage.setItem('auth_token', data.token);
            localStorage.setItem('user_data', JSON.stringify(data.user));
            
            // Verify storage worked
            const storedToken = localStorage.getItem('auth_token');
            if (!storedToken) {
              throw new Error('Storage verification failed');
            }
          } catch (storageError) {
            console.error('Failed to store auth data:', storageError);
            toast({ 
              title: "Storage Error", 
              description: "Cannot save login. Please enable cookies/localStorage in Edge settings (Settings > Cookies and site permissions).",
              variant: "destructive"
            });
            setLoading(false);
            return;
          }
          
          axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
          
          toast({ title: "Success", description: "Logged in successfully!" });
          
          // Small delay before redirect for Edge stability
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Force page reload to ensure clean state
          window.location.href = '/dashboard';
          return;
        } else {
          throw new Error('Invalid response from server - missing token or user data');
        }
      } else {
        const success = await register({ username, email, password });
        if (success) {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = error.message || "Please check your credentials and try again";
      
      // Provide helpful messages for network errors
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        errorMessage = "Network error. Please check your internet connection and browser settings.";
      }
      
      toast({ 
        title: "Login Failed", 
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const result = await forgotPassword(forgotEmail);
    if (result?.reset_token) {
      setResetToken(result.reset_token);
      setShowPasswordReset(true);
    }
    setShowForgotPassword(false);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    const success = await resetPassword(resetToken, newPassword);
    if (success) {
      setShowPasswordReset(false);
      setResetToken('');
      setNewPassword('');
    }
  };

  // Key platform capabilities (no numeric claims)
  const capabilities = [
    { label: 'Digital Safety Forms', icon: FileText },
    { label: 'AI Hazard Detection', icon: Eye },
    { label: 'OSHA Compliance Tools', icon: Shield },
  ];

  // Key benefits
  const benefits = [
    {
      icon: FileText,
      title: 'Replace Paper & Spreadsheets',
      description: 'Digitize all safety forms, PTPs, JHAs, and incident reports in one place'
    },
    {
      icon: AlertTriangle,
      title: 'Proactive Risk Detection',
      description: 'AI identifies hazards before they become costly incidents'
    },
    {
      icon: BarChart3,
      title: 'Compliance Documentation',
      description: 'Generate OSHA 300 logs, compliance reports, and documentation'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Built-in tools guide your team through compliance requirements'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{fontFamily: "'Inter', sans-serif"}}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center">
            <img 
              src="https://wsafeai.github.io/wsafeai-procore-prototype/brand/products/wsafe-ai/wsafe-ai-lockup-light.svg" 
              alt="WSafe AI" 
              className="h-8"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{backgroundColor: '#17475F'}}>
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold" style={{color: '#17475F'}}>WSafe AI</span>
            </div>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          {/* Sign In Card */}
          <Card className="bg-white shadow-lg border border-gray-200 rounded-2xl overflow-hidden">
            <CardHeader className="text-center pb-2 pt-8 px-8">
              <CardTitle className="text-2xl font-semibold" style={{color: '#17475F'}}>
                Sign in to WSafe AI
              </CardTitle>
              <CardDescription className="text-gray-500 mt-2">
                {showForgotPassword ? 'Enter your email to reset password' :
                 showPasswordReset ? 'Enter your new password' :
                 isLogin ? 'Welcome back' : 'Create your account'}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-8 pb-8 pt-4">
              {showForgotPassword ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email" className="text-gray-700">Email</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                      placeholder="you@company.com"
                      className="h-12 rounded-xl border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 text-white font-medium rounded-xl"
                    style={{backgroundColor: '#F28C1E'}}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#e07d15'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#F28C1E'}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Reset Link
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowForgotPassword(false)}
                    className="w-full text-gray-600 hover:text-gray-800"
                  >
                    Back to Sign In
                  </Button>
                </form>
              ) : showPasswordReset ? (
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-token" className="text-gray-700">Reset Token</Label>
                    <Input
                      id="reset-token"
                      type="text"
                      value={resetToken}
                      onChange={(e) => setResetToken(e.target.value)}
                      required
                      placeholder="Enter reset token from email"
                      className="h-12 rounded-xl border-gray-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password" className="text-gray-700">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      placeholder="Enter new password"
                      className="h-12 rounded-xl border-gray-300"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 text-white font-medium rounded-xl"
                    style={{backgroundColor: '#F28C1E'}}
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Reset Password
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {setShowPasswordReset(false); setResetToken(''); setNewPassword('');}}
                    className="w-full text-gray-600"
                  >
                    Back to Sign In
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isLogin && (
                    <div className="space-y-2">
                      <Label htmlFor="username" className="text-gray-700">Full Name</Label>
                      <Input
                        id="username"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        placeholder="John Smith"
                        className="h-12 rounded-xl border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700">Work Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@company.com"
                      className="h-12 rounded-xl border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-gray-700">Password</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="h-12 rounded-xl border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    />
                  </div>
                  
                  {!isLogin && (
                    <p className="text-xs text-gray-500" data-testid="register-role-note">
                      New accounts start with standard access. An admin in your organization can change your role.
                    </p>
                  )}
                  
                  <Button 
                    type="submit" 
                    className="w-full h-12 text-white font-medium rounded-xl transition-all"
                    style={{backgroundColor: '#F28C1E'}}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#e07d15'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#F28C1E'}
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                    ) : null}
                    {isLogin ? 'Sign In' : 'Create Account'}
                  </Button>
                  
                  {isLogin && (
                    <>
                      <div className="relative my-5">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="bg-white px-3 text-gray-500">or continue with</span>
                        </div>
                      </div>
                      
                      <WsafeSignInButton />
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-12 border-gray-300 hover:bg-gray-50 font-medium rounded-xl"
                        onClick={() => {
                          const redirectUrl = window.location.origin + '/auth/callback';
                          window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                        }}
                        data-testid="google-login-btn"
                      >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                      </Button>
                      
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="w-full text-center text-gray-500 hover:text-gray-700 text-sm mt-3"
                      >
                        Forgot Password?
                      </button>
                    </>
                  )}
                </form>
              )}
              
              {!showForgotPassword && !showPasswordReset && (
                <>
                  <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="bg-white px-3 text-gray-500">or</span>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => setIsLogin(!isLogin)}
                    className="w-full text-center font-medium transition-colors"
                    style={{color: '#F28C1E'}}
                    onMouseOver={(e) => e.target.style.color = '#e07d15'}
                    onMouseOut={(e) => e.target.style.color = '#F28C1E'}
                  >
                    {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
                  </button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Info Text Below Card */}
          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              New to WorkSafeAgent? Learn more at{' '}
              <a 
                href="https://worksafeagent.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-medium hover:underline"
                style={{color: '#17475F'}}
              >
                worksafeagent.com
              </a>
              {' '}or get the mobile app at{' '}
              <a 
                href="https://wsafe.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-medium hover:underline"
                style={{color: '#17475F'}}
              >
                wsafe.ai
              </a>
              .
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">
            © 2026 WorkSafeAgent LLC
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-gray-500 hover:text-gray-700 text-sm">
              Privacy
            </Link>
            <Link to="/terms" className="text-gray-500 hover:text-gray-700 text-sm">
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
// Navigation Component
const Navigation = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState(['main']);
  
  // Organized navigation with categories
  const navigationSections = [
    {
      id: 'main',
      title: 'Main',
      items: [
        { name: 'Dashboard', href: '/dashboard', icon: Home },
        { name: 'Safety Analytics', href: '/safety-analytics', icon: BarChart3 },
        { name: 'AI Analytics', href: '/analytics-dashboard', icon: Brain },
        { name: 'AI Insights', href: '/ai-insights', icon: Lightbulb },
        { name: 'Safety Meetings', href: '/safety-meetings', icon: Video },
      ]
    },
    {
      id: 'documentation',
      title: 'Documentation',
      items: [
        { name: 'AI Safety Generator', href: '/ai-safety-generator', icon: Sparkles },
        { name: 'JHA Builder', href: '/jha-builder', icon: FileText },
        { name: 'Permit to Work', href: '/permits', icon: Lock },
        { name: 'Daily PTP', href: '/daily-ptp', icon: ClipboardList },
      ]
    },
    {
      id: 'osha',
      title: 'OSHA Compliance',
      items: [
        { name: 'Safety Programs', href: '/safety-programs', icon: Shield },
        { name: 'OSHA Checklists', href: '/osha-checklists', icon: ClipboardCheck },
        { name: 'OSHA 300 Log', href: '/osha-300-log', icon: FileSpreadsheet },
        { name: 'OSHA Resources', href: '/osha-logs', icon: BookOpen },
        { name: 'OSHA Regulations', href: '/osha-regulations', icon: Scale },
        { name: 'Compliance Calendar', href: '/compliance-calendar', icon: Bell },
      ]
    },
    {
      id: 'incidents',
      title: 'Incidents & Hazards',
      items: [
        { name: 'Incidents', href: '/incidents', icon: AlertTriangle },
        { name: 'First Report of Injury', href: '/injury-report', icon: Flame },
        { name: 'Near-Miss Reports', href: '/near-miss', icon: AlertCircle },
        { name: 'Quick Hazard', href: '/quick-hazard', icon: Zap },
        { name: 'Risk Matrix', href: '/risk-matrix', icon: Target },
        { name: 'Risk Trends', href: '/risk-trends', icon: TrendingUp },
        { name: 'Investigations', href: '/incident-investigations', icon: FileSearch },
        { name: 'AI Hazard Scan', href: '/ai-hazard-scan', icon: Camera },
        { name: 'Hazard Alerts', href: '/hazard-alerts', icon: Bell },
      ]
    },
    {
      id: 'management',
      title: 'Management',
      items: [
        { name: 'CAPA', href: '/capa', icon: ClipboardCheck },
        { name: 'Project Board', href: '/projects', icon: LayoutGrid },
        { name: 'Workflows', href: '/workflows', icon: GitBranch },
      ]
    },
    {
      id: 'inspections',
      title: 'Inspections',
      items: [
        { name: 'Observations', href: '/observations', icon: Eye },
        { name: 'Equipment Log', href: '/equipment-log', icon: Wrench },
        { name: 'Audits', href: '/audits', icon: ClipboardCheck },
        { name: 'Safety Scorecard', href: '/safety-scorecard', icon: Target },
        { name: 'Export Scorecard', href: '/safety-scorecard/export', icon: FileText },
      ]
    },
    {
      id: 'training',
      title: 'Training',
      items: [
        { name: 'Training Management', href: '/training-management', icon: GraduationCap },
        { name: 'Training Center', href: '/training', icon: BookOpen },
        { name: 'Toolbox Talks', href: '/toolbox-talks', icon: Users },
      ]
    },
    {
      id: 'resources',
      title: 'Resources',
      items: [
        { name: 'Contractors', href: '/contractors', icon: HardHat },
        { name: 'Chemical Inventory', href: '/chemical-inventory', icon: Beaker },
        { name: 'Safety Calendar', href: '/safety-calendar', icon: Calendar },
        { name: 'Suggestion Box', href: '/suggestion-box', icon: Lightbulb },
      ]
    },
    {
      id: 'ai',
      title: 'AI Tools',
      items: [
        { name: 'Knowledge Center', href: '/knowledge-center', icon: Brain },
        { name: 'AI Assistant', href: '/ai-assistant', icon: MessageSquare },
        { name: 'CCTV Monitoring', href: '/cctv-monitoring', icon: Camera },
        { name: 'Safety Alerts', href: '/safety-alerts', icon: AlertTriangle },
        { name: 'Alert Integrations', href: '/alert-integrations', icon: Zap },
        { name: 'Translation', href: '/translation', icon: Languages },
      ]
    },
    {
      id: 'integrations',
      title: 'Integrations',
      items: [
        { name: 'Employee Import', href: '/employee-import', icon: Users },
        { name: 'IoT Sensors', href: '/iot-sensors', icon: Radio },
        { name: 'Workflow Automation', href: '/workflows', icon: Zap },
      ]
    },
    {
      id: 'fieldmode',
      title: 'Field Mode',
      items: [
        { name: 'Field Dashboard', href: '/field', icon: Smartphone },
        { name: 'Quick Incident', href: '/field/incident', icon: AlertTriangle },
      ]
    },
    {
      id: 'reports',
      title: 'Reports',
      items: [
        { name: 'Executive Dashboard', href: '/executive-dashboard', icon: BarChart3 },
        { name: 'Report Generator', href: '/report-generator', icon: FileText },
        { name: 'Reports', href: '/reports', icon: TrendingUp },
        { name: 'ROI Dashboard', href: '/roi', icon: DollarSign },
        { name: 'Audit Trail', href: '/audit-trail', icon: Clock },
        { name: 'Email Notifications', href: '/email-notifications', icon: Mail },
        { name: 'Achievements', href: '/gamification', icon: Trophy },
      ]
    },
  ];

  // Add admin section for admin users
  if (user?.role === 'admin') {
    navigationSections.push({
      id: 'admin',
      title: 'Admin',
      items: [
        { name: 'Admin Dashboard', href: '/admin', icon: Shield },
        { name: 'Company Management', href: '/admin/companies', icon: Building },
        { name: 'Alerts & Reports', href: '/admin/alerts-reports', icon: Bell },
        { name: 'Tour Analytics', href: '/admin/tour-analytics', icon: BarChart3 },
        { name: 'Role Management', href: '/roles', icon: UserCog },
        { name: 'Companies (Legacy)', href: '/companies', icon: Building },
      ]
    });
  }

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <div className="bg-slate-900 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-slate-700">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center mr-3">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">WSafe AI</h1>
            <p className="text-xs text-slate-400">Safety Management</p>
          </div>
        </div>
        <button 
          onClick={onClose}
          className="md:hidden p-2 rounded-lg hover:bg-slate-800 text-slate-400"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {navigationSections.map((section) => {
          const isExpanded = expandedSections.includes(section.id);
          const hasActiveItem = section.items.some(item => location.pathname === item.href);
          
          return (
            <div key={section.id} className="mb-2">
              <button
                onClick={() => toggleSection(section.id)}
                className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-lg transition-colors ${
                  hasActiveItem ? 'text-blue-400 bg-slate-800' : 'text-slate-400 hover:text-slate-300 hover:bg-slate-800'
                }`}
              >
                {section.title}
                <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </button>
              
              {isExpanded && (
                <div className="mt-1 space-y-0.5">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;
                    
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={handleNavClick}
                        className={`flex items-center px-3 py-2 rounded-lg text-sm transition-all ${
                          isActive
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        <Icon className={`w-4 h-4 mr-3 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      
      {/* User Section */}
      <div className="px-3 py-4 border-t border-slate-700">
        <div className="flex items-center justify-between bg-slate-800 rounded-lg p-3">
          <div className="flex items-center">
            <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-medium text-sm">
                {user?.username?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-white">{user?.username || 'User'}</p>
              <p className="text-xs text-slate-400">{user?.role || 'user'}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-slate-400 hover:text-white hover:bg-slate-700"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Quick Links */}
        <div className="flex gap-2 mt-3">
          <Link 
            to="/help" 
            onClick={handleNavClick}
            className="flex-1 flex items-center justify-center gap-1 py-2 text-xs text-slate-400 hover:text-white bg-slate-800 rounded-lg transition-colors"
          >
            <HelpCircle className="w-3 h-3" />
            Help
          </Link>
          <Link 
            to="/billing" 
            onClick={handleNavClick}
            className="flex-1 flex items-center justify-center gap-1 py-2 text-xs text-slate-400 hover:text-white bg-slate-800 rounded-lg transition-colors"
          >
            <CreditCard className="w-3 h-3" />
            Billing
          </Link>
        </div>
      </div>
    </div>
  );
};

// Removed duplicate Dashboard component - using imported Dashboard from ./components/Dashboard

// Companies Management Component
const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    address: '',
    phone: '',
    email: ''
  });
  const { toast } = useToast();

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load companies",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      const companyData = {
        ...formData,
        contact_info: {
          phone: formData.phone,
          email: formData.email
        }
      };
      delete companyData.phone;
      delete companyData.email;

      await axios.post(`${API}/companies`, companyData);
      toast({
        title: "Success",
        description: "Company created successfully!"
      });
      setShowCreateForm(false);
      setFormData({ name: '', industry: '', address: '', phone: '', email: '' });
      fetchCompanies();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create company",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
          <p className="text-gray-600">Manage companies for safety audits</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Company
        </Button>
      </div>

      {/* Create Company Form Modal */}
      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Company</DialogTitle>
            <DialogDescription>
              Enter company details for safety audit management
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCompany} className="space-y-4">
            <div>
              <Label htmlFor="name">Company Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="industry">Industry</Label>
              <Input
                id="industry"
                value={formData.industry}
                onChange={(e) => setFormData({...formData, industry: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">Create Company</Button>
              <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Companies List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {companies.map((company) => (
          <Card key={company.id} className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Building className="w-5 h-5 mr-2 text-blue-600" />
                {company.name}
              </CardTitle>
              <CardDescription>{company.industry}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm text-gray-600">
                <p>{company.address}</p>
                {company.contact_info?.phone && <p>📞 {company.contact_info.phone}</p>}
                {company.contact_info?.email && <p>✉️ {company.contact_info.email}</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {companies.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No companies yet</h3>
            <p className="text-gray-600 mb-4">Add your first company to start managing safety audits</p>
            <Button onClick={() => setShowCreateForm(true)}>Add Company</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Audits Management Component
const Audits = () => {
  const [audits, setAudits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    company_id: '',
    audit_name: ''
  });
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [auditsResponse, companiesResponse] = await Promise.all([
        axios.get(`${API}/audits`),
        axios.get(`${API}/companies`)
      ]);
      setAudits(auditsResponse.data);
      setCompanies(companiesResponse.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateAudit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/audits`, formData);
      toast({
        title: "Success",
        description: "Audit created successfully!"
      });
      setShowCreateForm(false);
      setFormData({ company_id: '', audit_name: '' });
      fetchData();
      // Navigate to the new audit
      navigate(`/audits/${response.data.id}`);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create audit",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Safety Audits</h1>
          <p className="text-gray-600">Manage OSHA compliance audits</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          New Audit
        </Button>
      </div>

      {/* Create Audit Form Modal */}
      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Audit</DialogTitle>
            <DialogDescription>
              Start a new OSHA compliance audit for a company
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAudit} className="space-y-4">
            <div>
              <Label htmlFor="company_id">Company</Label>
              <Select value={formData.company_id} onValueChange={(value) => setFormData({...formData, company_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name} - {company.industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="audit_name">Audit Name</Label>
              <Input
                id="audit_name"
                value={formData.audit_name}
                onChange={(e) => setFormData({...formData, audit_name: e.target.value})}
                placeholder="e.g., Q1 2024 Safety Compliance Audit"
                required
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1" disabled={!formData.company_id}>
                Create Audit
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Audits List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {audits.map((audit) => {
          const company = companies.find(c => c.id === audit.company_id);
          return (
            <Card key={audit.id} className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/audits/${audit.id}`)}>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-blue-600" />
                  {audit.audit_name}
                </CardTitle>
                <CardDescription>{company?.name || 'Unknown Company'}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <Badge 
                      variant={audit.status === 'completed' ? 'default' : 'secondary'}
                      className={audit.status === 'completed' ? 'bg-green-100 text-green-800' : ''}
                    >
                      {audit.status}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Created:</span>
                    <span className="text-sm text-gray-900">
                      {new Date(audit.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {audit.total_estimated_cost && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Est. Cost:</span>
                      <span className="text-sm font-medium text-green-600">
                        ${audit.total_estimated_cost.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {audits.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No audits yet</h3>
            <p className="text-gray-600 mb-4">Create your first safety audit to get started</p>
            <Button onClick={() => setShowCreateForm(true)} disabled={companies.length === 0}>
              {companies.length === 0 ? 'Add Companies First' : 'Create Audit'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Safety Image Analysis Component
const SafetyAnalysis = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const { toast } = useToast();

  const fetchAnalyses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/safety/analyses`);
      setAnalyses(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load analyses",
        variant: "destructive"
      });
    }
  }, [toast]);

  useEffect(() => {
    fetchAnalyses();
  }, [fetchAnalyses]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const analyzeImage = async () => {
    if (!selectedFile) {
      toast({
        title: "Error",
        description: "Please select an image first",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/safety/analyze-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setAnalysisResults(response.data);
      toast({
        title: "Success",
        description: "Image analysis completed successfully!"
      });
      
      // Refresh analyses list
      fetchAnalyses();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Image analysis failed",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Safety Analysis</h1>
          <p className="text-gray-600">Upload workplace images for AI-powered OSHA compliance analysis</p>
        </div>
      </div>

      {/* Image Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Camera className="w-5 h-5 mr-2 text-blue-600" />
            Upload Safety Image
          </CardTitle>
          <CardDescription>
            Upload photos of workplace conditions for AI analysis of OSHA violations and safety concerns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className="cursor-pointer flex flex-col items-center space-y-2"
              >
                <Upload className="w-12 h-12 text-gray-400" />
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-blue-600 hover:text-blue-500">
                    Click to upload
                  </span>{" "}
                  or drag and drop
                </div>
                <p className="text-xs text-gray-500">PNG, JPG, JPEG up to 10MB</p>
              </label>
            </div>

            {previewUrl && (
              <div className="space-y-4">
                <div className="max-w-md mx-auto">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                </div>
                <div className="text-center">
                  <Button
                    onClick={analyzeImage}
                    disabled={loading}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Brain className="w-4 h-4 mr-2" />
                        Analyze Safety
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Analysis Results */}
      {analysisResults && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Scan className="w-5 h-5 mr-2 text-green-600" />
              Analysis Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Risk Level */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-700">Overall Risk Level:</span>
                <Badge className={getRiskColor(analysisResults.analysis_results?.risk_level)}>
                  {analysisResults.analysis_results?.risk_level?.toUpperCase() || 'MEDIUM'}
                </Badge>
              </div>

              {/* Compliance Score */}
              {analysisResults.analysis_results?.compliance_score && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Compliance Score</span>
                    <span className="text-sm text-gray-500">
                      {analysisResults.analysis_results.compliance_score}%
                    </span>
                  </div>
                  <Progress value={analysisResults.analysis_results.compliance_score} className="h-2" />
                </div>
              )}

              {/* Violations Detected */}
              {analysisResults.analysis_results?.violations_detected?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Safety Violations Detected</h4>
                  <div className="space-y-3">
                    {analysisResults.analysis_results.violations_detected.map((violation, index) => (
                      <Card key={index} className="border-l-4 border-l-red-500">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">{violation.violation}</h5>
                              <p className="text-sm text-gray-600 mt-1">
                                OSHA Reference: {violation.osha_reference}
                              </p>
                            </div>
                            <Badge 
                              variant={violation.severity === 'high' ? 'destructive' : 'secondary'}
                              className="ml-2"
                            >
                              {violation.severity}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* PPE Assessment */}
              {analysisResults.analysis_results?.ppe_assessment && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">PPE Assessment</h4>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-700">PPE Compliant:</span>
                      <Badge variant={analysisResults.analysis_results.ppe_assessment.compliant ? 'default' : 'destructive'}>
                        {analysisResults.analysis_results.ppe_assessment.compliant ? 'Yes' : 'No'}
                      </Badge>
                    </div>
                    {analysisResults.analysis_results.ppe_assessment.missing_items?.length > 0 && (
                      <div>
                        <p className="text-sm text-blue-700 font-medium">Missing Items:</p>
                        <ul className="list-disc list-inside text-sm text-blue-600 mt-1">
                          {analysisResults.analysis_results.ppe_assessment.missing_items.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}</ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {analysisResults.analysis_results?.recommendations?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Recommendations</h4>
                  <div className="space-y-2">
                    {analysisResults.analysis_results.recommendations.map((rec, index) => (
                      <div key={index} className="flex items-start space-x-2 p-3 bg-green-50 rounded">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-green-700">{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Overall Assessment */}
              {analysisResults.analysis_results?.overall_assessment && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Overall Assessment</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">
                      {analysisResults.analysis_results.overall_assessment}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Previous Analyses */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Image className="w-5 h-5 mr-2 text-gray-600" />
            Previous Analyses
          </CardTitle>
        </CardHeader>
        <CardContent>
          {analyses.length > 0 ? (
            <div className="space-y-4">
              {analyses.map((analysis) => (
                <div key={analysis.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{analysis.image_name}</h4>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                      <span>Violations: {analysis.violations_count}</span>
                      <span>{new Date(analysis.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <Badge className={getRiskColor(analysis.risk_level)}>
                    {analysis.risk_level?.toUpperCase()}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No analyses found. Upload an image to get started!</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Audit Detail Component
const AuditDetail = () => {
  const { id } = useLocation().pathname.split('/').pop();
  const [audit, setAudit] = useState(null);
  const [company, setCompany] = useState(null);
  const [complianceItems, setComplianceItems] = useState([]);
  const [auditResults, setAuditResults] = useState([]);
  const [costAnalysis, setCostAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('checklist');
  const [loadingAI, setLoadingAI] = useState(false);
  const { toast } = useToast();

  const fetchAuditData = useCallback(async () => {
    try {
      // Get audit info
      const auditResponse = await axios.get(`${API}/audits`);
      const auditData = auditResponse.data.find(a => a.id === id);
      setAudit(auditData);

      if (auditData) {
        // Get company info
        const companiesResponse = await axios.get(`${API}/companies`);
        const companyData = companiesResponse.data.find(c => c.id === auditData.company_id);
        setCompany(companyData);

        // Get compliance items and results
        const [itemsResponse, resultsResponse] = await Promise.all([
          axios.get(`${API}/audits/${id}/compliance-items`),
          axios.get(`${API}/audits/${id}/results`)
        ]);
        
        setComplianceItems(itemsResponse.data);
        setAuditResults(resultsResponse.data);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load audit data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    if (id) {
      fetchAuditData();
    }
  }, [id, fetchAuditData]);

  const handleResultSubmit = async (itemId, status, notes = '', priority = 'medium') => {
    try {
      await axios.post(`${API}/audits/${id}/results`, {
        compliance_item_id: itemId,
        status,
        notes,
        priority
      });
      
      toast({
        title: "Success",
        description: "Audit result saved successfully!"
      });
      
      // Refresh results
      const resultsResponse = await axios.get(`${API}/audits/${id}/results`);
      setAuditResults(resultsResponse.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save audit result",
        variant: "destructive"
      });
    }
  };

  const generateAICostAnalysis = async () => {
    setLoadingAI(true);
    try {
      const response = await axios.post(`${API}/audits/${id}/ai-cost-analysis`);
      setCostAnalysis(response.data);
      setActiveTab('analysis');
      toast({
        title: "Success",
        description: "AI cost analysis generated successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to generate AI analysis",
        variant: "destructive"
      });
    } finally {
      setLoadingAI(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-medium text-gray-900">Audit not found</h2>
        <Button onClick={() => window.location.href = '/audits'} className="mt-4">
          Back to Audits
        </Button>
      </div>
    );
  }

  const getItemResult = (itemId) => {
    return auditResults.find(r => r.compliance_item_id === itemId);
  };

  const nonCompliantCount = auditResults.filter(r => r.status === 'non_compliant').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={() => window.location.href = '/audits'} className="mb-2">
            ← Back to Audits
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">{audit.audit_name}</h1>
          <p className="text-gray-600">{company?.name} - {company?.industry}</p>
        </div>
        {nonCompliantCount > 0 && (
          <Button 
            onClick={generateAICostAnalysis} 
            disabled={loadingAI}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {loadingAI ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating...
              </>
            ) : (
              <>
                <Brain className="w-4 h-4 mr-2" />
                AI Cost Analysis
              </>
            )}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="checklist">OSHA Checklist</TabsTrigger>
          <TabsTrigger value="results">Audit Results</TabsTrigger>
          <TabsTrigger value="analysis">Cost Analysis</TabsTrigger>
        </TabsList>

        {/* OSHA Checklist Tab */}
        <TabsContent value="checklist" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="w-5 h-5 mr-2 text-blue-600" />
                OSHA Compliance Checklist
              </CardTitle>
              <CardDescription>
                Comprehensive safety audit covering all major OSHA categories
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Group items by category */}
                {Array.from(new Set(complianceItems.map(item => item.category))).map(category => (
                  <div key={category} className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 capitalize border-b pb-2">
                      {category.replace(/_/g, ' ')}
                    </h3>
                    <div className="grid gap-4">
                      {complianceItems
                        .filter(item => item.category === category)
                        .map(item => {
                          const result = getItemResult(item.id);
                          return (
                            <Card key={item.id} className={`border-l-4 ${
                              result?.status === 'compliant' ? 'border-l-green-500' :
                              result?.status === 'non_compliant' ? 'border-l-red-500' :
                              result?.status === 'partial' ? 'border-l-yellow-500' :
                              'border-l-gray-300'
                            }`}>
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h4 className="font-medium text-gray-900">{item.item_name}</h4>
                                    <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                      <span>OSHA: {item.osha_reference}</span>
                                      <span>Level: {item.requirement_level}</span>
                                      {item.estimated_cost && <span>Est. Cost: ${item.estimated_cost}</span>}
                                    </div>
                                  </div>
                                  <div className="flex gap-2 ml-4">
                                    <Button
                                      size="sm"
                                      variant={result?.status === 'compliant' ? 'default' : 'outline'}
                                      onClick={() => handleResultSubmit(item.id, 'compliant')}
                                      className={result?.status === 'compliant' ? 'bg-green-600 hover:bg-green-700' : ''}
                                    >
                                      <CheckCircle className="w-4 h-4 mr-1" />
                                      Compliant
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant={result?.status === 'non_compliant' ? 'default' : 'outline'}
                                      onClick={() => handleResultSubmit(item.id, 'non_compliant', 'Requires attention', 'high')}
                                      className={result?.status === 'non_compliant' ? 'bg-red-600 hover:bg-red-700' : ''}
                                    >
                                      <AlertTriangle className="w-4 h-4 mr-1" />
                                      Non-Compliant
                                    </Button>
                                  </div>
                                </div>
                                {result && (
                                  <div className="mt-3 p-3 bg-gray-50 rounded">
                                    <div className="flex items-center justify-between text-sm">
                                      <Badge variant={
                                        result.status === 'compliant' ? 'default' :
                                        result.status === 'non_compliant' ? 'destructive' : 'secondary'
                                      }>
                                        {result.status}
                                      </Badge>
                                      <span className="text-gray-600">
                                        Priority: {result.priority}
                                      </span>
                                    </div>
                                    {result.notes && (
                                      <p className="text-sm text-gray-700 mt-1">{result.notes}</p>
                                    )}
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Results Tab */}
        <TabsContent value="results" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Audit Results Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{complianceItems.length}</div>
                  <div className="text-sm text-blue-700">Total Items</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {auditResults.filter(r => r.status === 'compliant').length}
                  </div>
                  <div className="text-sm text-green-700">Compliant</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{nonCompliantCount}</div>
                  <div className="text-sm text-red-700">Non-Compliant</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {auditResults.length > 0 ? Math.round((auditResults.filter(r => r.status === 'compliant').length / auditResults.length) * 100) : 0}%
                  </div>
                  <div className="text-sm text-purple-700">Compliance Score</div>
                </div>
              </div>

              {auditResults.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">Recent Results</h3>
                  {auditResults.slice(0, 10).map(result => {
                    const item = complianceItems.find(i => i.id === result.compliance_item_id);
                    return (
                      <div key={result.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <div>
                          <h4 className="font-medium text-gray-900">{item?.item_name}</h4>
                          <p className="text-sm text-gray-600">{result.notes}</p>
                        </div>
                        <Badge variant={
                          result.status === 'compliant' ? 'default' :
                          result.status === 'non_compliant' ? 'destructive' : 'secondary'
                        }>
                          {result.status}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Cost Analysis Tab */}
        <TabsContent value="analysis" className="space-y-6">
          {costAnalysis ? (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Brain className="w-5 h-5 mr-2 text-purple-600" />
                    AI-Powered Cost Analysis
                  </CardTitle>
                  <CardDescription>
                    Intelligent cost estimation for implementing OSHA compliance measures
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-lg">
                      <div className="text-2xl font-bold">${costAnalysis.total_estimated_cost?.toFixed(2)}</div>
                      <div className="text-green-100">Total Estimated Cost</div>
                    </div>
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg">
                      <div className="text-2xl font-bold">{costAnalysis.item_count}</div>
                      <div className="text-blue-100">Items Analyzed</div>
                    </div>
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 rounded-lg">
                      <div className="text-2xl font-bold">
                        {costAnalysis.cost_breakdown?.reduce((total, item) => total + item.implementation_hours, 0) || 0}
                      </div>
                      <div className="text-purple-100">Total Hours</div>
                    </div>
                  </div>

                  {costAnalysis.cost_breakdown && (
                    <div className="space-y-4">
                      <h3 className="font-medium text-gray-900">Cost Breakdown by Item</h3>
                      {costAnalysis.cost_breakdown.map((item, index) => {
                        const complianceItem = complianceItems.find(ci => ci.id === item.item_id);
                        return (
                          <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">{complianceItem?.item_name}</h4>
                              <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                                <span>Priority: {item.priority}</span>
                                <span>Implementation: {item.implementation_hours}h</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-green-600">
                                ${item.estimated_cost?.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {costAnalysis.ai_analysis && (
                    <div className="mt-6">
                      <h3 className="font-medium text-gray-900 mb-3">AI Analysis Report</h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {typeof costAnalysis.ai_analysis === 'string' ? 
                            costAnalysis.ai_analysis : 
                            JSON.stringify(costAnalysis.ai_analysis, null, 2)
                          }
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Brain className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">AI Cost Analysis</h3>
                <p className="text-gray-600 mb-4">
                  Generate AI-powered cost estimates for non-compliant items
                </p>
                {nonCompliantCount > 0 ? (
                  <Button onClick={generateAICostAnalysis} disabled={loadingAI}>
                    {loadingAI ? 'Generating...' : 'Generate AI Analysis'}
                  </Button>
                ) : (
                  <p className="text-sm text-gray-500">
                    Complete the audit checklist first to generate cost analysis
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Main Layout Component
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Global keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Advanced Search Modal */}
      <AdvancedSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} isDarkMode={false} />
      
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar - hidden on mobile, shown on md+ */}
      <div className="hidden md:block w-64 flex-shrink-0">
        <Navigation />
      </div>
      
      {/* Mobile sidebar - slide in from left */}
      <div className={`fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out md:hidden ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <Navigation isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      </div>
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header with Search */}
        <header className="bg-white border-b px-4 md:px-6 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 md:gap-4">
            {/* Mobile menu button */}
            <button 
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            {/* Search Button */}
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Search className="w-4 h-4" />
              <span className="hidden md:inline">Search...</span>
              <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs bg-gray-200 rounded">
                ⌘K
              </kbd>
            </button>
            <NotificationBell />
            <GlobalSearch />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-3 md:p-6">
          {children}
        </main>
        {/* Footer - positioned inside main content area */}
        <footer className="bg-white border-t py-4 px-6 flex-shrink-0">
          <div className="flex flex-col md:flex-row items-center justify-between text-sm text-gray-600 gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <span>© 2025-2026 WorkSafeAgent LLC.</span>
              <span className="text-gray-300">|</span>
              <span className="text-amber-600 font-medium text-xs">USPTO 63/992,261</span>
              <span className="text-gray-300">|</span>
              <a href="https://doi.org/10.5281/zenodo.20499515" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 text-xs">
                DOI: 10.5281/zenodo.20499515
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a 
                href="/privacy-policy" 
                className="hover:text-blue-600 transition-colors"
              >
                Privacy
              </a>
              <span className="text-gray-300">|</span>
              <a 
                href="/terms-of-service" 
                className="hover:text-blue-600 transition-colors"
              >
                Terms
              </a>
              <span className="text-gray-300">|</span>
              <a 
                href="https://worksafeagent.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-blue-600 transition-colors flex items-center gap-1"
              >
                worksafeagent.com
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </footer>
      </div>
      {/* Voice Commands - Global */}
      <VoiceCommands isEnabled={true} />
      {/* AI Assistant Floating Button */}
      <FloatingChatButton />
    </div>
  );
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <PWAProvider>
        <FieldModeProvider>
        <div className="App">
          <Routes>
            {/* Public Landing Page at root */}
            <Route path="/" element={<PublicLandingPage />} />
            {/* Login page */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/wsafe-callback" element={<WsafeAuthCallback />} />
            {/* Phase 6 - Field Mode Routes (No Layout - Standalone Mobile UI) */}
            <Route path="/field" element={
              <ProtectedRoute>
                <FieldModeDashboard />
              </ProtectedRoute>
            } />
            <Route path="/field/incident" element={
              <ProtectedRoute>
                <MovedToInductIQ module="Incident reporting" />
              </ProtectedRoute>
            } />
            <Route path="/field/ptp" element={
              <ProtectedRoute>
                <MobilePTPForm />
              </ProtectedRoute>
            } />
            <Route path="/field/near-miss" element={
              <ProtectedRoute>
                <MobileNearMissForm />
              </ProtectedRoute>
            } />
            <Route path="/field/jha" element={
              <ProtectedRoute>
                <MobileJHAForm />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/observations" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <ObservationsPage />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/companies" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <CompaniesPage />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/audits" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <AuditsPage />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/audits/:id" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <EnhancedAuditDetail />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/safety-analysis" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <AdvancedSafetyAI />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/safety-analysis/:id" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <AnalysisDetailPage />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/safety-ops" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <SafetyOperationsCenter />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/monitoring" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <SafetyMonitoringHub />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/live-monitoring" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <LiveMonitoringPage />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <ReportsPage />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/osha-logs" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <MovedToInductIQ module="OSHA logs" />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/osha-logs/create" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <MovedToInductIQ module="OSHA logs" />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/osha-logs/:id" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <MovedToInductIQ module="OSHA logs" />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/advanced-tools" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <AdvancedToolsPage />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/knowledge-center" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <EnhancedKnowledgeCenter />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/osha-regulations" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <OSHARegulationsSearch />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/training" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <MovedToInductIQ module="Training" />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/training/classic" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <MovedToInductIQ module="Training" />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/documents" element={
              <ProtectedRoute>
                <SubscriptionGate>
                  <Layout>
                    <DocumentManagement />
                  </Layout>
                </SubscriptionGate>
              </ProtectedRoute>
            } />
            <Route path="/subscription" element={
              <ProtectedRoute>
                <Layout>
                  <SubscriptionPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/billing" element={
              <ProtectedRoute>
                <Layout>
                  <BillingPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/billing-legacy" element={
              <ProtectedRoute>
                <Layout>
                  <BillingManagementPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/roi" element={
              <ProtectedRoute>
                <Layout>
                  <ROIDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/predictive" element={
              <ProtectedRoute>
                <Layout>
                  <PredictiveAnalytics />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/analytics-intelligence" element={
              <ProtectedRoute>
                <Layout>
                  <AnalyticsIntelligenceDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/gamification" element={
              <ProtectedRoute>
                <Layout>
                  <GamificationDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/translation" element={
              <ProtectedRoute>
                <Layout>
                  <TranslationHub />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/integrations" element={
              <ProtectedRoute>
                <Layout>
                  <IntegrationsHub />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Role Management (RBAC) */}
            <Route path="/roles" element={
              <ProtectedRoute>
                <Layout>
                  <RoleManagement />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Incident Reporting */}
            <Route path="/incidents" element={
              <ProtectedRoute>
                <Layout>
                  <MovedToInductIQ module="Incident reporting" />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Scheduled Reports */}
            <Route path="/scheduled-reports" element={
              <ProtectedRoute>
                <Layout>
                  <ScheduledReportsPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* AI Assistant */}
            <Route path="/ai-assistant" element={
              <ProtectedRoute>
                <Layout>
                  <AIAssistantPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Safety Scorecard */}
            <Route path="/safety-scorecard" element={
              <ProtectedRoute>
                <Layout>
                  <SafetyScorecardPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Safety Scorecard Export */}
            <Route path="/safety-scorecard/export" element={
              <ProtectedRoute>
                <Layout>
                  <SafetyScorecardExportPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Near-Miss Reporting */}
            <Route path="/near-miss" element={
              <ProtectedRoute>
                <Layout>
                  <NearMissReportingPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Safety Calendar */}
            <Route path="/safety-calendar" element={
              <ProtectedRoute>
                <Layout>
                  <SafetyCalendarPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Toolbox Talks */}
            <Route path="/toolbox-talks" element={
              <ProtectedRoute>
                <Layout>
                  <ToolboxTalksPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Contractor Management */}
            <Route path="/contractors" element={
              <ProtectedRoute>
                <Layout>
                  <ContractorManagementPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Chemical Inventory */}
            <Route path="/chemical-inventory" element={
              <ProtectedRoute>
                <Layout>
                  <ChemicalInventoryPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Quick Win Features - Phase 2 */}
            <Route path="/daily-ptp" element={
              <ProtectedRoute>
                <Layout>
                  <DailyPTPPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/equipment-log" element={
              <ProtectedRoute>
                <Layout>
                  <EquipmentLogPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/quick-hazard" element={
              <ProtectedRoute>
                <Layout>
                  <QuickHazardPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/suggestion-box" element={
              <ProtectedRoute>
                <Layout>
                  <SuggestionBoxPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/ai-hazard-scan" element={
              <ProtectedRoute>
                <Layout>
                  <AIHazardScanPage />
                </Layout>
              </ProtectedRoute>
            } />
            {/* Public shared scan view - no authentication required */}
            <Route path="/shared/scan/:shareToken" element={<ProtectedRoute><SharedScanPage /></ProtectedRoute>} />
            <Route path="/hazard-alerts" element={
              <ProtectedRoute>
                <Layout>
                  <HazardAlertsPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Phase 2 - Comprehensive Features */}
            <Route path="/jha-builder" element={
              <ProtectedRoute>
                <Layout>
                  <JHABuilderPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/safety-analytics" element={
              <ProtectedRoute>
                <Layout>
                  <SafetyAnalyticsPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/analytics-dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <DashboardBuilder />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/employee-import" element={
              <ProtectedRoute>
                <Layout>
                  <EmployeeImportPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/iot-sensors" element={
              <ProtectedRoute>
                <Layout>
                  <IoTSensorDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/workflows" element={
              <ProtectedRoute>
                <Layout>
                  <WorkflowAutomationPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/projects" element={
              <ProtectedRoute>
                <Layout>
                  <ProjectManagementPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/injury-report" element={
              <ProtectedRoute>
                <Layout>
                  <MovedToInductIQ module="First report of injury" />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/safety-meetings" element={
              <ProtectedRoute>
                <Layout>
                  <SafetyMeetingsPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/permits" element={
              <ProtectedRoute>
                <Layout>
                  <PermitToWorkPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/training-management" element={
              <ProtectedRoute>
                <Layout>
                  <MovedToInductIQ module="Training management" />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/incident-investigations" element={
              <ProtectedRoute>
                <Layout>
                  <MovedToInductIQ module="Incident investigations" />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* CAPA Management */}
            <Route path="/capa" element={
              <ProtectedRoute>
                <Layout>
                  <CAPAPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Safety Programs */}
            <Route path="/safety-programs" element={
              <ProtectedRoute>
                <Layout>
                  <SafetyProgramsPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* OSHA Checklists */}
            <Route path="/osha-checklists" element={
              <ProtectedRoute>
                <Layout>
                  <OSHAChecklistsPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Risk Matrix */}
            <Route path="/risk-matrix" element={
              <ProtectedRoute>
                <Layout>
                  <RiskMatrix />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Risk Trends */}
            <Route path="/risk-trends" element={
              <ProtectedRoute>
                <Layout>
                  <RiskTrendsPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* AI Enhanced Safety Generator */}
            <Route path="/ai-safety-generator" element={
              <ProtectedRoute>
                <Layout>
                  <AIEnhancedSafetyPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Phase 3 - Digital Signatures & Compliance */}
            <Route path="/osha-300-log" element={
              <ProtectedRoute>
                <Layout>
                  <MovedToInductIQ module="OSHA 300 log" />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/compliance-calendar" element={
              <ProtectedRoute>
                <Layout>
                  <ComplianceCalendarPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/notification-settings" element={
              <ProtectedRoute>
                <Layout>
                  <NotificationSettingsPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            
            {/* Safety Work Management */}
            <Route path="/safety-work" element={
              <ProtectedRoute>
                <Layout>
                  <SafetyWorkManagement />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Meetings */}
            <Route path="/meetings" element={
              <ProtectedRoute>
                <Layout>
                  <MeetingsPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/help" element={
              <ProtectedRoute>
                <Layout>
                  <HelpCenterPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/help/:articleId" element={
              <ProtectedRoute>
                <Layout>
                  <HelpCenterPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/support" element={
              <ProtectedRoute>
                <Layout>
                  <SupportPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <Layout>
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/companies" element={
              <ProtectedRoute>
                <Layout>
                  <CompanyManagementPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/companies/:companyId" element={
              <ProtectedRoute>
                <Layout>
                  <CompanyDetailPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/alerts-reports" element={
              <ProtectedRoute>
                <Layout>
                  <AlertsReportsPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/tour-analytics" element={
              <ProtectedRoute>
                <Layout>
                  <ProductTourAnalyticsDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            {/* Phase 8 - Advanced Reporting & Communication */}
            <Route path="/executive-dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <ExecutiveDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/audit-trail" element={
              <ProtectedRoute>
                <Layout>
                  <AuditTrailPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/report-generator" element={
              <ProtectedRoute>
                <Layout>
                  <ReportGeneratorPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/email-notifications" element={
              <ProtectedRoute>
                <Layout>
                  <EmailNotificationsPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Layout>
                  <SettingsPageWrapper />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/cctv-monitoring" element={
              <ProtectedRoute>
                <Layout>
                  <CCTVMonitoringPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/safety-alerts" element={
              <ProtectedRoute>
                <Layout>
                  <SafetyAlertsDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/alert-integrations" element={
              <ProtectedRoute>
                <Layout>
                  <AlertIntegrationsPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/ai-insights" element={
              <ProtectedRoute>
                <Layout>
                  <AIInsightsPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/payment-success" element={
              <ProtectedRoute>
                <PaymentSuccessPage />
              </ProtectedRoute>
            } />
            <Route path="/payment-canceled" element={
              <ProtectedRoute>
                <PaymentCanceledPage />
              </ProtectedRoute>
            } />
          </Routes>
          <Toaster />
        </div>
        </FieldModeProvider>
        </PWAProvider>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;